export type ThemeName = 'dark' | 'light';

export type Tokens = {
  bg: string;
  panel: string;
  panel2: string;
  border: string;
  borderStrong: string;
  text: string;
  muted: string;
  faint: string;
  primary: string;
  primaryStrong: string;
  success: string;
  warning: string;
  ai: string;
  scrollThumb: string;
  scrollThumbHover: string;
};

export const themes: Record<ThemeName, Tokens> = {
  dark: {
    bg: '#0F172A',
    panel: '#1B2540',
    panel2: '#243049',
    border: 'rgba(255,255,255,0.08)',
    borderStrong: 'rgba(255,255,255,0.14)',
    text: '#F8FAFC',
    muted: '#94A3B8',
    faint: '#64748B',
    primary: '#3B82F6',
    primaryStrong: '#60A5FA',
    success: '#10B981',
    warning: '#F59E0B',
    ai: '#A78BFA',
    scrollThumb: 'rgba(148,163,184,0.22)',
    scrollThumbHover: 'rgba(148,163,184,0.40)',
  },
  light: {
    bg: '#FFFFFF',
    panel: '#F8FAFC',
    panel2: '#F1F5F9',
    border: 'rgba(15,23,42,0.08)',
    borderStrong: 'rgba(15,23,42,0.14)',
    text: '#0F172A',
    muted: '#475569',
    faint: '#94A3B8',
    primary: '#2563EB',
    primaryStrong: '#1D4ED8',
    success: '#059669',
    warning: '#D97706',
    ai: '#7C3AED',
    scrollThumb: 'rgba(15,23,42,0.14)',
    scrollThumbHover: 'rgba(15,23,42,0.28)',
  },
};

// Task #1665 — scale Aurora estese per Chat/Feed/Workgroups (additive).
// Coprono spacing/radius/shadow/typography richieste dal design pass.
export const auroraSpace = {
  xxs: '0.125rem', // 2
  xs: '0.25rem',   // 4
  sm: '0.5rem',    // 8
  md: '0.75rem',   // 12
  lg: '1rem',      // 16
  xl: '1.5rem',    // 24
  '2xl': '2rem',   // 32
  '3xl': '2.5rem', // 40
} as const;

export const auroraRadius = {
  none: '0',
  sm: '0.375rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',
  '2xl': '1.25rem',
  full: '9999px',
} as const;

export const auroraShadow = {
  none: 'none',
  sm: '0 1px 2px rgba(15,23,42,0.06)',
  md: '0 4px 12px rgba(15,23,42,0.08)',
  lg: '0 12px 32px rgba(15,23,42,0.10)',
  glow: '0 0 0 1px rgba(99,102,241,0.20), 0 6px 24px rgba(99,102,241,0.15)',
} as const;

export const auroraType = {
  caption: 'text-[11px] leading-tight',
  body: 'text-sm leading-relaxed',
  bodyStrong: 'text-sm leading-relaxed font-semibold',
  title: 'text-base font-semibold leading-snug',
  headline: 'text-lg font-bold leading-snug',
  display: 'text-2xl font-bold leading-tight tracking-tight',
} as const;

export type AuroraSpace = keyof typeof auroraSpace;
export type AuroraRadius = keyof typeof auroraRadius;
export type AuroraShadow = keyof typeof auroraShadow;
export type AuroraTypeScale = keyof typeof auroraType;

// Task #1665 — palette Aurora canonica (single source of truth per
// illustrazioni, focus ring, glass gradient). Mai duplicare hex inline:
// importa da qui o usa le Tailwind class equivalenti.
export const auroraPalette = {
  indigo: '#6366F1',
  indigoStrong: '#4F46E5',
  violet: '#8B5CF6',
  violetSoft: '#A78BFA',
  fuchsia: '#EC4899',
  emerald: '#10B981',
  slate: '#94A3B8',
  slateSoft: 'rgba(148,163,184,0.55)',
  focusLight: 'rgba(99, 102, 241, 0.85)',
  focusDark: 'rgba(167, 139, 250, 0.9)',
} as const;
export type AuroraPaletteKey = keyof typeof auroraPalette;

export const aurora = {
  serif: '"Source Serif 4", Georgia, serif',
  sans: '"Inter", system-ui, sans-serif',
  mono: '"JetBrains Mono", monospace',
  ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
  spring: {
    soft: { type: 'spring' as const, stiffness: 220, damping: 24 },
    snappy: { type: 'spring' as const, stiffness: 380, damping: 30 },
    bouncy: { type: 'spring' as const, stiffness: 260, damping: 18 },
  },
  fadeUp: {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
  // Task #1665 — varianti motion riusabili per liste (entry soffusa)
  listItemIn: {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -4 },
    transition: { duration: 0.18, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
  accent: {
    ai: 'text-violet-500 dark:text-violet-400',
    default: 'text-blue-500 dark:text-blue-400',
  },
  font: {
    serifTenantBadge: 'font-serif',
  },
  space: auroraSpace,
  radius: auroraRadius,
  shadow: auroraShadow,
  type: auroraType,
  palette: auroraPalette,
  // Task #1665 — helper stagger per primi N elementi (motion entry liste).
  stagger: (index: number, max = 10, step = 0.04): number => Math.min(index, max) * step,
} as const;

export type AuroraTokens = typeof aurora;
